import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { X } from 'lucide-react-native';
import { useEffect } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { headerItem } from '@/components/HeaderActions';
import { SuccessScreen } from '@/components/SuccessScreen';
import { Header } from '@/components/header';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from '@/components/ui/modal';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { REFERRAL_COPY, INVITE_COPY_VERSION } from '@/constants/referrals';
import { ROUTES } from '@/constants/routes';
import { useInquiryClose } from '@/hooks/inbox/useInquiryClose';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { segmentClient } from '@/util/analytics';
import { navigateIntoTabs } from '@/util/navigateIntoTabs';

type Props = {
  isOpen?: boolean;
  onClose?: () => void;
  rating?: string;
};

type CopyAndCta = {
  copy: string;
  ctaCopy: string;
  ctaPath: '/directory' | '/account/invites';
};

const copyAndCtas: Record<'anotherQuestion' | 'inviteAPeer', CopyAndCta> = {
  anotherQuestion: {
    copy: 'Your feedback is essential to us improving the Primum experience',
    ctaCopy: 'Ask another question',
    ctaPath: '/directory',
  },
  inviteAPeer: {
    copy: REFERRAL_COPY.referrer.feedbackSuccessCopy,
    ctaCopy: 'Invite a Peer',
    ctaPath: '/account/invites',
  },
};

export default function FeedbackSuccess({ isOpen, onClose, rating: ratingProp }: Props) {
  const { navigateBack } = useBackNavigation(ROUTES.INBOX);

  const router = useRouter();

  const { bottom } = useSafeAreaInsets();

  const { rating: ratingParam } = useLocalSearchParams<{ rating?: string }>();

  const { state: closeState, completeFeedback, reset: resetCloseState } = useInquiryClose();

  const shouldNavigateAfterClose = closeState.navigationTarget?.shouldNavigate === true;

  /**
   * On mount, mobile only: For grouped flow, defer feedbackComplete until user
   * dismisses screen so they see the CTA. Non-grouped flow completes immediately.
   */
  useEffect(() => {
    if (isWeb) {
      return;
    }

    if (!shouldNavigateAfterClose) {
      completeFeedback();
    }
  }, [completeFeedback, shouldNavigateAfterClose]);

  const rating = ratingProp ?? ratingParam;

  const showInviteMsgAndCta = rating === '4' || rating === '5';

  const copyAndCta = copyAndCtas[showInviteMsgAndCta ? 'inviteAPeer' : 'anotherQuestion'];

  /**
   * Dismiss success screen.
   *
   * For grouped flow: dispatch COMPLETE_FEEDBACK action to transition state
   * to 'complete'. InquiryCloseNavigationEffect responds and handles navigation.
   *
   * For non-grouped flow: use normal back navigation.
   */
  const handleDismiss = () => {
    if (shouldNavigateAfterClose) {
      completeFeedback();
    } else {
      navigateBack();
    }
  };

  const handleCtaPress = () => {
    onClose?.();

    if (showInviteMsgAndCta) {
      segmentClient.track(ANALYTICS_EVENTS.FEEDBACK_SUCCESS_INVITE_A_PEER_LINK_CLICKED, {
        invite_copy_version: INVITE_COPY_VERSION,
        referral_location: 'feedback_success_modal',
        referral_role: 'referrer',
      });
    }

    if (shouldNavigateAfterClose) {
      resetCloseState();
    }

    if (copyAndCta.ctaPath === '/directory') {
      navigateIntoTabs(copyAndCta.ctaPath);

      return;
    }

    router.replace(copyAndCta.ctaPath);
  };

  if (isWeb) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalBackdrop />
        <ModalContent className="border-primary-500 bg-primary-500">
          <ModalHeader className="justify-end">
            <ModalCloseButton>
              <Icon as={X} size="lg" className="self-end text-typography-0" />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <SuccessScreen
              heading="Thank you!"
              description={copyAndCta.copy}
              riveAnimation="FEEDBACK_SUCCESS"
              buttonText={copyAndCta.ctaCopy}
              onButtonPress={handleCtaPress}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <VStack className="flex-1 bg-primary-500" style={{ paddingBottom: bottom }}>
      <Header fill="brand" coversActingAsBanner leftItems={[headerItem.close(handleDismiss)]} />

      <SuccessScreen
        heading="Thank you!"
        description={copyAndCta.copy}
        riveAnimation="FEEDBACK_SUCCESS"
        buttonText={copyAndCta.ctaCopy}
        onButtonPress={handleCtaPress}
      />
    </VStack>
  );
}
